<template>
  <teleport to="body">
    <!--
      UN BANDEAU QU'ON NE PEUT PAS MANQUER — celui de Plans d'actions et de
      l'outil IGD : toute la largeur, ambre, des rayures qui défilent lentement.
      Une couleur seule se fond au bout de deux minutes ; le mouvement rappelle
      en continu qu'on regarde des données FICTIVES.
      z-45 : AU-DESSUS de la page, SOUS les fenêtres de l'application (AltModal,
      z-50) — sinon il masquerait leur bouton « Enregistrer ».
    -->
    <div
      v-if="decouverte.actif"
      ref="bandeauRef"
      data-tour="bandeau-demo"
      class="bt-demo-bandeau fixed inset-x-0 bottom-0 z-[45] border-t-2 border-sunflower-gold/70"
      role="region"
      aria-label="Mode démo"
    >
      <div class="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <span class="flex min-w-0 items-center gap-2.5">
          <span class="bt-demo-pastille" aria-hidden="true" />
          <span class="min-w-0">
            <span class="block text-sm font-semibold text-space-indigo">Mode démo — données fictives</span>
            <span class="hidden text-xs text-space-indigo sm:block">Rien de ce que vous faites ici n'atteint les vraies données ; aucun courriel ne part.</span>
          </span>
        </span>

        <!-- Le rôle endossé : le circuit se comprend en passant d'une main à l'autre. -->
        <div class="flex items-center gap-1.5" role="radiogroup" aria-label="Rôle endossé dans la démo">
          <span class="hidden text-xs font-medium text-space-indigo lg:inline">Vous êtes :</span>
          <button
            v-for="p in personas"
            :key="p.id"
            type="button"
            role="radio"
            :aria-checked="decouverte.persona === p.id"
            :aria-label="`${p.nom}, ${p.role}`"
            class="bt-demo-role"
            :class="decouverte.persona === p.id ? 'bt-demo-role--actif' : ''"
            :disabled="bascule"
            @click="changer(p.id)"
          >
            <span class="bt-demo-initiales" aria-hidden="true">{{ p.initiales }}</span>
            <span class="hidden md:inline" aria-hidden="true">{{ p.role }}</span>
          </button>
        </div>

        <div class="ml-auto flex shrink-0 items-center gap-1.5">
          <button type="button" class="bt-demo-action" aria-label="Visite guidée" @click="router.push('/decouverte')">
            <span aria-hidden="true">🧭</span><span class="hidden sm:inline" aria-hidden="true"> Visite guidée</span>
          </button>
          <button type="button" class="bt-demo-action" :disabled="bascule" @click="remettreAZero">Réinitialiser</button>
          <button
            type="button"
            class="rounded-lg bg-space-indigo px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-space-indigo/90"
            :disabled="bascule"
            @click="sortir"
          >
            Quitter la démo
          </button>
        </div>
      </div>
      <!-- Ce que fait le rôle choisi, dit en une ligne (annoncé aux lecteurs d'écran). -->
      <p class="sr-only" aria-live="polite">{{ annonce }}</p>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'altoneo-charting';
import { PERSONA_LIBELLES, type PersonaId } from '@/demo/monde';
import { personasPour } from '@/components/decouverte/chapitres';
import { confirmer } from '@/lib/confirmer';
import { garantirFin, gsap, mouvementReduit } from '@/lib/mouvement';
import { useDecouverteStore } from '@/stores/decouverte';
import { useModeDemo } from '@/composables/useModeDemo';

const decouverte = useDecouverteStore();
const modeDemo = useModeDemo();
const router = useRouter();
const toast = useToast();

const bandeauRef = ref<HTMLElement | null>(null);
const bascule = ref(false);
const annonce = ref('');

// Hors staff, la cliente seule : le bandeau ne propose pas les rôles de l'équipe.
const personas = computed(() =>
  personasPour(decouverte.natureReelle).map((id) => {
    const l = PERSONA_LIBELLES[id];
    return { id, role: l.role, nom: l.nom, initiales: l.nom.split(' ').map((m) => m[0]).join('') };
  }),
);

async function changer(p: PersonaId): Promise<void> {
  if (bascule.value || p === decouverte.persona) return;
  bascule.value = true;
  try {
    await modeDemo.changerPersona(p);
    const l = PERSONA_LIBELLES[p];
    annonce.value = `Vous êtes ${l.nom}, ${l.role.toLowerCase()} : ${l.resume}.`;
    toast.info(annonce.value);
  } finally {
    bascule.value = false;
  }
}

async function remettreAZero(): Promise<void> {
  const ok = await confirmer({
    titre: 'Réinitialiser la démo',
    message: 'La démo revient à son état de départ.',
    details: ['Vos demandes, interventions, exports et relevés de démo sont effacés.', 'Les vraies données ne sont pas concernées.'],
    action: 'Réinitialiser',
    danger: false,
  });
  if (!ok) return;
  bascule.value = true;
  try {
    await modeDemo.reinitialiser();
    toast.success('La démo est revenue à son état de départ.');
  } finally {
    bascule.value = false;
  }
}

async function sortir(): Promise<void> {
  bascule.value = true;
  try {
    await modeDemo.quitter();
  } finally {
    bascule.value = false;
  }
}

/*
 * Le bandeau est FIXE : sans compensation il masquerait le bas de chaque page.
 * La classe posée sur `body` réserve sa hauteur, et disparaît avec la démo.
 */
const CLASSE = 'bt-demo-on';
watch(
  () => decouverte.actif,
  async (actif) => {
    document.body.classList.toggle(CLASSE, actif);
    if (actif && !mouvementReduit()) {
      await nextTick();
      if (bandeauRef.value) garantirFin(gsap.from(bandeauRef.value, { yPercent: 100, duration: 0.5, ease: 'power3.out' }));
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => document.body.classList.remove(CLASSE));
</script>
